import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import socketService from '../services/socket';
import styles from './TodayModal.module.css';
import LoadingSpinner from './LoadingSpinner';
import StepTimeline from './StepTimeline';
import RapportModal from './RapportModal';
import DayTimeline from './DayTimeline';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

const TodayModal = ({ isOpen, onClose, buckets }) => {
  const [todayData, setTodayData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isClosing, setIsClosing] = useState(false);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isFadingOutLoadingScreen, setIsFadingOutLoadingScreen] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState(new Set());
  const [editingEntry, setEditingEntry] = useState(null);
  const [editTimes, setEditTimes] = useState({ startTime: '', endTime: '' });
  const [isRapportModalOpen, setIsRapportModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // Subtile Animation bei Datum-Wechsel
  const [dataVersion, setDataVersion] = useState(0); // Incrementiert bei jedem erfolgreichen Fetch -> Content-Remount mit Animation
  const prevSelectedDate = useRef(selectedDate);
  // Fix 30.10.2026: Guard gegen das Socket-Echo des eigenen Step-Adds. Der
  // Server emitted ticketUpdated auch fuer eigene POSTs - ohne Guard wuerde
  // jedes selbst hinzugefuegte Step einen (silent) Refetch und damit einen
  // Content-Umbau ausloesen. State wird stattdessen lokal gepatcht.
  const selfStepAddRef = useRef({ ticketId: null, until: 0 });

  const fetchDayData = async (date, silent = false) => {
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsContentLoaded(false);
    }
    try {
      const response = await axios.get(`${API_URL}/timetracking/daily/${date}`);
      setTodayData(response.data);
      // Fix 30.10.2026: Content-Remount nur bei echten Loadern (Datumwechsel),
      // silent Updates bauen das Modal nicht neu auf
      if (!silent) {
        setDataVersion(v => v + 1); // Content-Animation triggern
      }
      if (silent) {
        setIsRefreshing(false);
      } else {
        setTimeout(() => {
          setIsFadingOutLoadingScreen(true);
          setTimeout(() => {
            setIsLoading(false);
            setTimeout(() => {
              setIsContentLoaded(true);
              setIsFadingOutLoadingScreen(false);
            }, 50);
          }, 300);
        }, 800);
      }
    } catch (error) {
      console.error('Error fetching day data:', error);
      // Fix 05.10.2026: Liste immer leeren (auch bei silent), sonst bleibt unter
      // einem neuen Datum die alte Liste stehen (eingefrorene Today-Ansicht)
      setTodayData([]);
      if (silent) {
        setIsRefreshing(false);
      } else {
        setTimeout(() => {
          setIsFadingOutLoadingScreen(true);
          setTimeout(() => {
            setIsLoading(false);
            setTimeout(() => {
              setIsContentLoaded(true);
              setIsFadingOutLoadingScreen(false);
            }, 50);
          }, 300);
        }, 800);
      }
    }
  };

  // Ein useEffect fuer beides: unterscheidet "Modal geoeffnet" vs "Datum geaendert"
  useEffect(() => {
    if (!isOpen) {
      prevSelectedDate.current = selectedDate;
      return;
    }

    if (selectedDate !== prevSelectedDate.current) {
      // Datum wurde geaendert (Modal war schon offen) -> silent refresh mit Animation
      fetchDayData(selectedDate, true);
    } else {
      // Modal wurde geoeffnet -> full loading
      setIsContentLoaded(false);
      setIsClosing(false);
      setIsLoading(true);
      setExpandedTickets(new Set());
      fetchDayData(selectedDate);
    }
    prevSelectedDate.current = selectedDate;
  }, [isOpen, selectedDate]);

  useEffect(() => {
    if (!isOpen) return;

    const handleTicketUpdated = (ticket) => {
      // Eigenes Step-Add: State wurde schon lokal gepatcht, kein Refetch noetig
      const guard = selfStepAddRef.current;
      if (guard.ticketId && String(ticket._id) === String(guard.ticketId) && Date.now() < guard.until) {
        return;
      }

      const isRelevant = todayData.some(group => group.ticket._id === ticket._id);

      if (isRelevant) {
        // Fix 30.10.2026: still aktualisieren, kein Loading-Screen/Remount mehr
        fetchDayData(selectedDate, true);
      }
    };

    socketService.on('ticketUpdated', handleTicketUpdated);

    return () => {
      socketService.off('ticketUpdated', handleTicketUpdated);
    };
  }, [isOpen, selectedDate, todayData]);

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300); // Match animation duration
  };

  // No longer grouping by bucket - just use todayData directly

  // Calculate total time for the day
  const calculateTotalTime = () => {
    const totalSeconds = todayData.reduce((acc, group) => {
      return acc + group.entries.reduce((entryAcc, entry) => entryAcc + entry.duration, 0);
    }, 0);
    return formatDuration(totalSeconds);
  };

  // Calculate total time for a specific ticket
  const calculateTicketTotalTime = (ticketGroup) => {
    const totalSeconds = ticketGroup.entries.reduce((acc, entry) => acc + entry.duration, 0);
    return formatDuration(totalSeconds);
  };

  // Toggle expanded state for a ticket
  const toggleTicketExpanded = (ticketId) => {
    const newExpandedTickets = new Set(expandedTickets);
    if (newExpandedTickets.has(ticketId)) {
      newExpandedTickets.delete(ticketId);
    } else {
      newExpandedTickets.add(ticketId);
    }
    setExpandedTickets(newExpandedTickets);
  };

  // Start editing a time entry
  const startEditingEntry = (entryId, entry) => {
    setEditingEntry(entryId);
    const startTime = new Date(entry.startTime);
    const endTime = entry.endTime ? new Date(entry.endTime) : new Date();
    
    // Format times to HH:MM for input
    const formatTimeForInput = (date) => {
      return date.toTimeString().slice(0, 5);
    };
    
    setEditTimes({
      startTime: formatTimeForInput(startTime),
      endTime: formatTimeForInput(endTime)
    });
  };

  // Save edited times using the new PUT API route
  const saveEditedTimes = async (entry) => {
    try {
      const today = selectedDate;
      const startDateTime = new Date(`${today}T${editTimes.startTime}:00`);
      const endDateTime = new Date(`${today}T${editTimes.endTime}:00`);
      
      // Calculate new duration in seconds
      const newDuration = Math.floor((endDateTime - startDateTime) / 1000);
      
      if (newDuration <= 0) {
        alert('End time must be after start time');
        return;
      }

      // Update the entry using the new PUT API
      await axios.put(`${API_URL}/timetracking/${entry._id}`, {
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        duration: newDuration
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Refresh data
      fetchDayData(selectedDate);
      setEditingEntry(null);
    } catch (error) {
      console.error('Error updating time entry:', error);
      if (error.response) {
        alert(`Error: ${error.response.data.error || 'Failed to update time entry'}`);
      } else {
        alert('Error updating time entry. Please try again.');
      }
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingEntry(null);
    setEditTimes({ startTime: '', endTime: '' });
  };

  if (!isOpen) return null;

  if (isLoading) {
    return <LoadingSpinner fadeOut={isFadingOutLoadingScreen} />;
  }

  return (
    <div className={`${styles.overlay} ${isClosing ? styles.closing : ''}`}>
      <div className={`${styles.modal} ${isClosing ? styles.closing : ''}`} style={{
        opacity: isContentLoaded ? 1 : 0,
        transform: isContentLoaded ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(30px)',
        transition: isContentLoaded ? 'all 0.2s ease-out' : 'none'
      }}>
        <div className={styles.header}>
          <h2>Arbeitszeit Übersicht</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {todayData.length > 0 && (
              <button
                onClick={() => setIsRapportModalOpen(true)}
                className={styles.aiRapportButton}
                title="AI Rapport erstellen"
              >
                <i className="bi bi-magic"></i> AI Rapport
              </button>
            )}
            <button onClick={handleClose} className={styles.closeButton}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
        
        <div className={styles.dateSelector}>
          <label htmlFor="dateInput">Datum:</label>
          <input
            id="dateInput"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className={styles.dateInput}
          />
        </div>

        <div 
          key={dataVersion} 
          className={`${styles.content} ${styles.contentAnimate}`}
          style={{
            opacity: isRefreshing ? 0.35 : 1,
            transition: 'opacity 0.25s ease',
            pointerEvents: isRefreshing ? 'none' : 'auto',
          }}
        >
          {/* Visual Timeline */}
          {todayData.length > 0 && (
            <DayTimeline timeEntries={todayData} />
          )}

          {todayData.length === 0 ? (
            <p className={styles.noData}>Keine Zeiterfassung für dieses Datum gefunden.</p>
          ) : (
            <div className={styles.bucketGroups}>
              <div className={styles.bucketGroup}>
                <div className={styles.bucketHeader}>
                  <h3>Heute</h3>
                  <div className={styles.bucketTotalTime}>
                    Total: {calculateTotalTime()}
                  </div>
                </div>
                <div className={styles.ticketGroups}>
                  {todayData.map((ticketGroup, ticketIndex) => {
                    const ticketId = `ticket-${ticketIndex}`;
                    const isExpanded = expandedTickets.has(ticketId) || ticketGroup.entries.length === 0;

                    return (
                      <div key={ticketIndex} className={styles.ticketGroup}>
                        <div
                          className={`${styles.ticketHeader} ${styles.clickable}`}
                          onClick={() => toggleTicketExpanded(ticketId)}
                        >
                          <div className={styles.ticketInfo}>
                            <div className={styles.dropdownIcon}>
                              {isExpanded ? '▼' : '▶'}
                            </div>
                            <h4 className={styles.ticketTitle}>{ticketGroup.ticket.title}</h4>
                            <div className={styles.ticketMeta}>
                              {(ticketGroup.ticket.projectRef?.name || ticketGroup.ticket.project) && (
                                <span className={styles.project}>📁 {ticketGroup.ticket.projectRef?.name || ticketGroup.ticket.project}</span>
                              )}
                              {ticketGroup.ticket.priority && (
                                <span className={`${styles.priority} ${styles[ticketGroup.ticket.priority?.toLowerCase()]}`}>
                                  {ticketGroup.ticket.priority}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className={styles.totalTime}>
                            {calculateTicketTotalTime(ticketGroup)}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className={styles.timeEntries}>
                            {ticketGroup.entries.map((entry, entryIndex) => {
                              const entryId = `${ticketIndex}-${entryIndex}`;
                              const isEditing = editingEntry === entryId;

                              return (
                                <div key={entryIndex} className={`${styles.timeEntry} ${styles.hoverable}`}>
                                  {isEditing ? (
                                    // Edit Mode
                                    <>
                                      <div className={styles.entryInfo}>
                                        <div className={styles.entryTime}>
                                          <input
                                            type="time"
                                            value={editTimes.startTime}
                                            onChange={(e) => setEditTimes({...editTimes, startTime: e.target.value})}
                                            className={styles.timeInput}
                                          />
                                          <span> - </span>
                                          <input
                                            type="time"
                                            value={editTimes.endTime}
                                            onChange={(e) => setEditTimes({...editTimes, endTime: e.target.value})}
                                            className={styles.timeInput}
                                          />
                                          <div className={styles.editButtons}>
                                            <button
                                              onClick={() => saveEditedTimes(entry)}
                                              className={styles.saveButton}
                                              title="Save"
                                            >
                                              <i className="bi bi-check-lg"></i>
                                            </button>
                                            <button
                                              onClick={cancelEditing}
                                              className={styles.cancelButton}
                                              title="Cancel"
                                            >
                                              <i className="bi bi-x-lg"></i>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div className={styles.entryDuration}>
                                        {formatDuration(entry.duration)}
                                      </div>
                                      {entry.stepText && (
                                        <div className={styles.stepInfo}>
                                          <span className={styles.stepLabel}>Step:</span> {entry.stepText}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    // View Mode
                                    <>
                                      <div className={styles.entryInfo}>
                                        <div className={styles.entryTime}>
                                          <span className={styles.startTime}>
                                            {new Date(entry.startTime).toLocaleTimeString()}
                                          </span>
                                          <span> - </span>
                                          <span className={entry.endTime ? styles.endTime : styles.running}>
                                            {entry.endTime ? new Date(entry.endTime).toLocaleTimeString() : 'Läuft...'}
                                          </span>
                                          <button
                                            className={styles.editButton}
                                            onClick={() => startEditingEntry(entryId, entry)}
                                            title="Edit time entry"
                                          >
                                            <i className="bi bi-pencil"></i>
                                          </button>
                                        </div>
                                      </div>
                                      <div className={styles.entryDuration}>
                                        {formatDuration(entry.duration)}
                                      </div>
                                      {entry.stepText && (
                                        <div className={styles.stepInfo}>
                                          <span className={styles.stepLabel}>Step:</span> {entry.stepText}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}

                            {/* Step-Timeline */}
                            <StepTimeline
                              steps={ticketGroup.ticket.steps || []}
                              stepTimestamps={ticketGroup.ticket.stepTimestamps || []}
                              ticketId={ticketGroup.ticket._id}
                              selectedDate={selectedDate}
                              onStepAdding={() => {
                                // Socket-Echo-Guard setzen, BEVOR der POST losgeht
                                selfStepAddRef.current = {
                                  ticketId: ticketGroup.ticket._id,
                                  until: Date.now() + 3000
                                };
                              }}
                              onStepAdded={(data) => {
                                // Fix 30.10.2026: Step direkt in den State patchen -
                                // kein Refetch, kein Modal-Remount, sofort sichtbar
                                if (!data || !Array.isArray(data.steps)) return;
                                const exists = todayData.some(g => g.ticket._id === ticketGroup.ticket._id);
                                setTodayData(prev => prev.map(group => {
                                  if (group.ticket._id !== ticketGroup.ticket._id) return group;
                                  return {
                                    ...group,
                                    ticket: {
                                      ...group.ticket,
                                      steps: data.steps,
                                      stepTimestamps: data.stepTimestamps || group.ticket.stepTimestamps
                                    }
                                  };
                                }));
                                // Ticket war an diesem Datum noch nicht geladen
                                // (kein Zeit-/Step-Eintrag) -> Gruppe still nachladen
                                if (!exists) {
                                  fetchDayData(selectedDate, true);
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rapport Modal */}
      <RapportModal
        isOpen={isRapportModalOpen}
        onClose={() => setIsRapportModalOpen(false)}
        todayData={todayData}
        selectedDate={selectedDate}
        buckets={buckets}
      />
    </div>
  );
};

export default TodayModal;